import {
  Autocomplete,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useStatuteApi } from '../../services/api/useStatute.api';
import { ApiResponse } from '../../types/ApiResponse.type';
import { Statute } from '../../types/Statute.type';
import { capitalizeFirstLetter } from '../../utils/capitalizeFirstLetter';

interface StatuteAutocompleteProps {
  countyId?: number | null;
  value: Statute[];
  onChange: (newValue: Statute[]) => void;
  disabled?: boolean;
  placeholder?: string;
  isEnhancement?: boolean;
  isCaseEnhancement?: boolean;
  isChargeEnhancement?: boolean;
  singleSelect?: boolean;
  autocompleteId?: string;
  openAutocompleteId?: string | null;
  onOpenChange?: (id: string | null) => void;
  onSelectAllRequest?: (statutes: Statute[], isDeselecting: boolean) => void;
}

function StatuteAutocomplete({
  countyId,
  value,
  onChange,
  disabled = false,
  placeholder,
  isEnhancement,
  isCaseEnhancement,
  isChargeEnhancement,
  singleSelect = false,
  autocompleteId,
  openAutocompleteId,
  onOpenChange,
  onSelectAllRequest,
}: StatuteAutocompleteProps) {
  const { getStatutes } = useStatuteApi();
  const [inputValue, setInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const { ref, inView } = useInView();
  const timeoutId = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Check if it is controlled (ComparatorTool) or uncontrolled (SearchPanel)
  const isControlled = autocompleteId !== undefined && openAutocompleteId !== undefined && onOpenChange !== undefined;
  const effectiveIsOpen = isControlled ? openAutocompleteId === autocompleteId : isOpen;

  const {
    data: infiniteStatutes,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery({
    queryKey: [
      'statutes',
      countyId,
      isEnhancement === true ? 'enhancement' : null,
      isCaseEnhancement === true ? 'case_enhancement' : null,
      isChargeEnhancement === true ? 'charge_enhancement' : null,
      autocompleteId,
      searchQuery,
    ],
    queryFn: async ({ pageParam = 1 }) => {
      return await getStatutes({
        page: pageParam,
        per_page: 100,
        county_id: countyId || undefined,
        query: searchQuery,
        ...(isEnhancement !== undefined && { is_enhancement: isEnhancement }),
        ...(isCaseEnhancement !== undefined && { is_case_enhancement: isCaseEnhancement }),
        ...(isChargeEnhancement !== undefined && { is_charge_enhancement: isChargeEnhancement }),
      });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: ApiResponse<Statute[]>) => {
      if ((lastPage?.meta.pagination?.page ?? 0) < (lastPage?.meta.pagination?.pages ?? 0)) {
        return (lastPage?.meta.pagination?.page ?? 0) + 1;
      }
      return undefined;
    },
    enabled: isControlled ? !!autocompleteId && openAutocompleteId === autocompleteId : isOpen,
  });

  const allStatutes = useMemo(() => {
    const paginated = infiniteStatutes?.pages?.flatMap((page) => page.data) ?? [];

    if (!searchQuery && value.length > 0) {
      const statuteMap = new Map<number, Statute>();
      paginated.forEach((s) => statuteMap.set(s.id, s));

      value.forEach((selectedStatute) => {
        if (!statuteMap.has(selectedStatute.id)) {
          statuteMap.set(selectedStatute.id, selectedStatute);
        }
      });

      return Array.from(statuteMap.values());
    }

    return paginated;
  }, [infiniteStatutes, value, searchQuery]);

  const handleSearchInputChange = useCallback((newValue: string) => {
    setInputValue(newValue);

    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
    }

    const delay = 500;
    timeoutId.current = setTimeout(() => {
      setSearchQuery(newValue);
    }, delay);
  }, []);

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleSelectAll = useCallback(() => {
    const availableStatutes = allStatutes.filter((option) => option.cases_count === null || option.cases_count >= 1);
    const allSelected =
      availableStatutes.length > 0 && availableStatutes.every((opt) => value.some((v) => v.id === opt.id));

    if (allSelected) {
      if (onSelectAllRequest) {
        onSelectAllRequest(availableStatutes, true);
      } else {
        const remaining = value.filter((v) => !availableStatutes.some((opt) => opt.id === v.id));
        onChange(remaining);
      }
    } else {
      const newStatutes = availableStatutes.filter((opt) => !value.some((v) => v.id === opt.id));
      if (onSelectAllRequest) {
        onSelectAllRequest(newStatutes, false);
      } else {
        onChange([...value, ...newStatutes]);
      }
    }
  }, [allStatutes, value, onChange, onSelectAllRequest]);

  const handleChange = useCallback(
    (_: React.SyntheticEvent, newValue: Statute[]) => {
      if (singleSelect) {
        onChange(newValue.length > 0 ? [newValue[newValue.length - 1]] : []);
      } else {
        onChange(newValue);
      }
    },
    [onChange, singleSelect],
  );

  const handleOpen = useCallback(() => {
    if (isControlled && onOpenChange && autocompleteId) {
      onOpenChange(autocompleteId);
    } else {
      setIsOpen(true);
    }
  }, [isControlled, onOpenChange, autocompleteId]);

  const handleClose = useCallback(() => {
    setInputValue('');
    setSearchQuery('');
    if (isControlled && onOpenChange) {
      onOpenChange(null);
    } else {
      setIsOpen(false);
    }
  }, [isControlled, onOpenChange]);

  const effectiveLoading = isControlled ? isLoading && openAutocompleteId === autocompleteId : isLoading;

  const getPlaceholder = () => {
    if (placeholder) {
      return placeholder;
    }
    if (effectiveLoading) {
      return isEnhancement ? 'Loading enhancements...' : 'Loading statutes...';
    }
    if (value.length === 0) {
      return 'Currently N/A';
    }
    return '';
  };

  return (
    <FormControl sx={{ flexGrow: 1, width: '100%' }}>
      <Autocomplete
        disableCloseOnSelect
        multiple
        open={effectiveIsOpen}
        onOpen={handleOpen}
        onClose={handleClose}
        disabled={disabled}
        loading={effectiveLoading}
        clearIcon={null}
        popupIcon={null}
        options={allStatutes}
        getOptionLabel={(option) => option.display_name}
        getOptionDisabled={(option) => option.cases_count !== null && option.cases_count < 1}
        value={value}
        onChange={handleChange}
        inputValue={inputValue}
        isOptionEqualToValue={(option, val) => option.id === val.id}
        filterOptions={(options) => options}
        onInputChange={(_, val, reason) => {
          if (reason === 'input') {
            handleSearchInputChange(val);
          } else if (reason === 'clear') {
            handleSearchInputChange('');
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            variant='outlined'
            placeholder={getPlaceholder()}
            slotProps={{
              ...params.slotProps,

              input: {
                ...params.slotProps.input,
                endAdornment: (
                  <>
                    {effectiveLoading ? <CircularProgress color='inherit' size={20} /> : null}
                    {params.slotProps.input.endAdornment}
                  </>
                ),
              },
            }}
            sx={{
              '& .MuiInputBase-root': {
                paddingRight: '9px !important',
              },
            }}
          />
        )}
        slotProps={{
          popper: {
            sx: {
              '& .MuiAutocomplete-option': {
                fontSize: '14px',
              },
              '& .MuiAutocomplete-noOptions': {
                fontSize: '14px',
              },
            },
          },
          paper: {
            sx: {
              width: 320,
            },
          },
          listbox: {
            onScroll: (event: React.SyntheticEvent) => {
              const listboxNode = event.currentTarget;
              const position = listboxNode.scrollTop + listboxNode.clientHeight;
              if (listboxNode.scrollHeight - position <= 1) {
                if (hasNextPage && !isFetchingNextPage) {
                  fetchNextPage();
                }
              }
            },
          },
        }}
        noOptionsText={
          effectiveLoading
            ? isEnhancement
              ? 'Loading enhancements...'
              : 'Loading statutes...'
            : isEnhancement
              ? 'No enhancement statutes found'
              : 'No statutes found'
        }
        renderOption={(props, option, state) => {
          const isFirstItem = state.index === 0;
          const isLastItem = state.index === allStatutes.length - 1;
          const availableStatutes = allStatutes.filter((opt) => opt.cases_count === null || opt.cases_count >= 1);
          const allSelected =
            availableStatutes.length > 0 && availableStatutes.every((opt) => value.some((v) => v.id === opt.id));

          return (
            <Fragment key={option.id}>
              {isFirstItem && searchQuery && (
                <li
                  key='select-all'
                  role='option'
                  aria-selected={allSelected}
                  onClick={handleSelectAll}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') handleSelectAll();
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    padding: '0px 8px 6px',
                    borderBottom: '1px solid #e0e0e0',
                    cursor: 'pointer',
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={allSelected}
                        indeterminate={
                          !allSelected &&
                          value.length > 0 &&
                          availableStatutes.some((opt) => value.some((v) => v.id === opt.id))
                        }
                        onChange={handleSelectAll}
                        size='small'
                      />
                    }
                    label={
                      <Typography variant='body2' sx={{ fontWeight: 600 }}>
                        {allSelected ? 'Unselect All' : 'Select All'}
                      </Typography>
                    }
                    sx={{ margin: 0 }}
                  />
                </li>
              )}
              <li
                {...props}
                key={option.id}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}
              >
                <div style={{ width: '100%' }}>
                  {option.display_name}
                  <br />
                  <Typography
                    variant='body2'
                    sx={{
                      fontStyle: 'italic',
                      color: '#718096',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      width: '100%',
                    }}
                    title={`${option.display_name} - ${option.description}`}
                  >
                    {capitalizeFirstLetter(option?.description?.toLowerCase() || '')}
                  </Typography>
                </div>
                {isLastItem && hasNextPage && (
                  <div
                    ref={ref}
                    style={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      padding: '8px',
                    }}
                  >
                    {isFetchingNextPage && <CircularProgress size={20} />}
                  </div>
                )}
              </li>
            </Fragment>
          );
        }}
      />
    </FormControl>
  );
}

export default StatuteAutocomplete;
